// Client Portal activity status — compact badge (lists/cards) + detailed panel (record view).
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Activity, BellRing, CalendarClock, CheckCircle2, Clock, Eye, FileWarning, Send, Sparkles } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export const ACTIVITY_TONE = {
  slate: { pill: 'bg-slate-100 text-slate-700 ring-slate-200', dot: 'bg-slate-400' },
  sky: { pill: 'bg-sky-50 text-sky-800 ring-sky-200', dot: 'bg-sky-500' },
  emerald: { pill: 'bg-emerald-50 text-emerald-800 ring-emerald-200', dot: 'bg-emerald-500 animate-pulse' },
  amber: { pill: 'bg-amber-50 text-amber-800 ring-amber-200', dot: 'bg-amber-500' },
  indigo: { pill: 'bg-indigo-50 text-indigo-800 ring-indigo-200', dot: 'bg-indigo-500' },
  violet: { pill: 'bg-violet-50 text-violet-800 ring-violet-200', dot: 'bg-violet-500' },
  green: { pill: 'bg-green-50 text-green-800 ring-green-200', dot: 'bg-green-600' },
  teal: { pill: 'bg-teal-50 text-teal-800 ring-teal-200', dot: 'bg-teal-600' },
  rose: { pill: 'bg-rose-50 text-rose-800 ring-rose-200', dot: 'bg-rose-500' },
  red: { pill: 'bg-red-50 text-red-800 ring-red-200', dot: 'bg-red-600' },
};

const mins = (iso) => (iso ? Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000)) : null);
export const ago = (iso) => {
  const m = mins(iso);
  if (m === null) return '—';
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  if (m < 1440) return `${Math.floor(m / 60)}h ${m % 60 ? `${m % 60}m ` : ''}ago`;
  return `${Math.floor(m / 1440)}d ago`;
};
const dur = (m) => (m === null ? '—' : m < 60 ? `${m} min` : m < 1440 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${Math.floor(m / 1440)}d ${Math.floor((m % 1440) / 60)}h`);
export const when = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  const today = new Date().toDateString() === d.toDateString();
  const t = d.toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit' });
  return today ? `Today, ${t}` : `${d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })}, ${t}`;
};

// Re-render every minute so "x min ago" stays live without refetching
export const useMinuteTick = () => {
  const [, set] = useState(0);
  useEffect(() => { const t = setInterval(() => set((n) => n + 1), 60000); return () => clearInterval(t); }, []);
};

const subline = (a) => {
  if (a.key === 'not_opened') return `Sent ${ago(a.sent_at)} · not opened`;
  if (a.key === 'opened') return `Opened ${ago(a.first_opened_at)} · nothing entered`;
  if (a.key === 'draft') return 'Invitation not sent yet';
  if (a.key === 'expired') return `Link expired · ${a.pct}% done`;
  if (['ready', 'completed'].includes(a.key)) return `${a.pct}% complete`;
  if (a.key === 'active') return `Active ${ago(a.last_active_at)} · ${a.pct}%`;
  return `${a.last_active_at ? `Inactive ${dur(mins(a.last_active_at))}` : 'Started'} · ${a.pct}%`;
};

export function PortalActivityBadge({ activity, testId, showSub = true }) {
  useMinuteTick();
  if (!activity) return null;
  const tone = ACTIVITY_TONE[activity.tone] || ACTIVITY_TONE.slate;
  const ir = activity.inactivity_reminder || {};
  return (
    <div className="min-w-0" data-testid={testId}>
      <span className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[10.5px] font-bold ring-1 whitespace-nowrap ${tone.pill}`} data-testid={testId && `${testId}-label`}>
        <span className={`w-1.5 h-1.5 rounded-full ${tone.dot}`} />{activity.label}
      </span>
      {showSub && (
        <p className="mt-0.5 flex items-center gap-1 text-[11px] text-slate-500 whitespace-nowrap tabular-nums" data-testid={testId && `${testId}-sub`}>
          {subline(activity)}
          {ir.sent_at && <span title={`30-min reminder sent ${when(ir.sent_at)}`} className="inline-flex items-center text-amber-600"><BellRing className="w-3 h-3" /></span>}
        </p>
      )}
    </div>
  );
}

const Row = ({ icon: Icon, label, value, testid, tone }) => (
  <div className="flex items-start gap-2.5 py-1.5" data-testid={testid}>
    <Icon className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${tone || 'text-slate-400'}`} />
    <span className="w-36 shrink-0 text-xs text-slate-500">{label}</span>
    <span className="text-xs font-semibold text-slate-800 min-w-0">{value}</span>
  </div>
);

export function PortalActivityPanel({ appId, headers, refreshKey }) {
  const [a, setA] = useState(null);
  useMinuteTick();
  const load = useCallback(() => axios.get(`${API}/api/onboarding/applications/${appId}/portal-activity`, { headers }).then(({ data }) => setA(data)).catch(() => {}), [appId, headers]);
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load, refreshKey]);
  if (!a) return null;
  const ir = a.inactivity_reminder || {};
  const dr = a.daily_reminder || {};
  const pctTone = a.pct >= 80 ? 'bg-emerald-500' : a.pct >= 40 ? 'bg-indigo-500' : 'bg-amber-500';
  return (
    <section className="rounded-2xl bg-white ring-1 ring-slate-200/80 shadow-sm p-4 sm:p-5" data-testid="portal-activity-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <span className="w-8 h-8 rounded-xl bg-[#15133A] text-white grid place-items-center cp-dark"><Activity className="w-4 h-4" /></span>
          <div><p className="text-sm font-black text-slate-900">Portal activity</p><p className="text-[11px] text-slate-500">Live from the client's real portal actions</p></div>
        </div>
        <PortalActivityBadge activity={a} testId="portal-activity-status" showSub={false} />
      </div>
      <div className="mt-4">
        <div className="flex items-center justify-between text-[11px] font-bold text-slate-500 mb-1"><span>Progress</span><span className="tabular-nums" data-testid="portal-activity-pct" title="Overall progress across the fact find, documents and consents">{a.pct}% completed · overall (form + docs)</span></div>
        <div className="h-2 rounded-full bg-slate-100 overflow-hidden"><div className={`h-full rounded-full transition-[width] duration-700 ${pctTone}`} style={{ width: `${a.pct}%` }} /></div>
      </div>
      <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-x-8 divide-y lg:divide-y-0 divide-slate-100">
        <div>
          <Row icon={Send} label="Invitation sent" value={when(a.sent_at)} testid="pa-sent" />
          <Row icon={Eye} label="Link opened" value={a.opened ? `Yes · first ${when(a.first_opened_at)}` : 'Not yet'} testid="pa-opened" tone={a.opened ? 'text-sky-500' : 'text-slate-400'} />
          <Row icon={Sparkles} label="Started entering info" value={a.started_at ? when(a.started_at) : ['opened', 'not_opened', 'draft'].includes(a.key) ? 'Not yet' : 'Yes'} testid="pa-started" />
          <Row icon={Clock} label="Last active" value={a.last_active_at ? `${when(a.last_active_at)} (${ago(a.last_active_at)})` : a.last_seen_at ? `Last seen ${when(a.last_seen_at)}` : '—'} testid="pa-last-active" />
          <Row icon={Clock} label="Inactive for" value={a.last_active_at && !['completed', 'ready'].includes(a.key) ? dur(mins(a.last_active_at)) : '—'} testid="pa-inactive-for" />
          <Row icon={FileWarning} label="Last section" value={a.last_section_title || '—'} testid="pa-last-section" />
        </div>
        <div>
          <Row icon={BellRing} label="30-min reminder" testid="pa-inactivity" tone={ir.sent_at ? 'text-amber-500' : 'text-slate-400'}
            value={ir.sent_at ? `Sent ${when(ir.sent_at)}${ir.status ? ` · ${ir.status.replace('_', ' ')}` : ''}` : ir.next_due_at && ir.eligible_now !== false ? `Due ${when(ir.next_due_at)}` : (ir.reason || 'Not due')} />
          {ir.sent_at && <Row icon={BellRing} label="Next 30-min reminder" value={ir.reason || '—'} testid="pa-inactivity-next" />}
          <Row icon={CalendarClock} label="Daily reminders" testid="pa-daily"
            value={dr.paused ? 'Paused by broker' : dr.stopped ? 'Stopped' : dr.last_sent_at ? `${dr.count} sent · last ${when(dr.last_sent_at)}` : 'Running · none sent yet'} />
          <div className="py-1.5" data-testid="pa-outstanding">
            <p className="flex items-center gap-2.5 text-xs text-slate-500"><CheckCircle2 className="w-3.5 h-3.5 text-slate-400" />Still outstanding · {a.outstanding_count}</p>
            {a.outstanding?.length ? <div className="mt-1.5 ml-6 flex flex-wrap gap-1">{a.outstanding.map((o) => <span key={o} className="rounded-md bg-slate-100 px-1.5 py-0.5 text-[11px] font-medium text-slate-700">{o}</span>)}</div>
              : <p className="ml-6 text-xs font-semibold text-emerald-700">Nothing outstanding</p>}
          </div>
        </div>
      </div>
    </section>
  );
}
